import { EditOutlined, PlusOutlined } from "@ant-design/icons"
import axiosInstance from "../../../../axios/axiosInstance"
import { useEffect, useState } from "react"
import { Input, Table, Tag } from "antd"
import type { ColumnsType } from "antd/es/table/interface"
import FeatureFormModal from "./FeatureFormModal"
import { ModePage } from "common/enum"
import processError from "../../../../utils/processError"
import useTableConfig from "../../../../hooks/useTableConfig"
import {FeatureCode} from "../../../../model/enum";
import {FeatureResponse} from "../../../../model/response";

export default function FeatureManagement() {
  const { pageSize, currentPage, sortBy, direction, pagination, setTotalElements, getColumnSorter, handleTableChange } =
    useTableConfig()
  const [keyword, setKeyword] = useState<string>("")
  const [featureId, setFeatureId] = useState<string>("")
  const [mode, setMode] = useState<ModePage>(ModePage.ADD)
  const [isOpen, setOpenModal] = useState<boolean>(false)

  const [data, setData] = useState<FeatureResponse[]>()
  const [loading, setLoading] = useState(false)

  const getFeatureCodeColor = (code: FeatureCode) => {
    const colorMap = {
      AUTO_SCORING: "blue",
      SKILL_EVALUATION: "green",
      TOPIC_PRACTICE: "orange",
      EXPERT_QA: "purple",
    }
    return colorMap[code] || "default"
  }

  const getFeatureCodeLabel = (code: FeatureCode) => {
    const labelMap = {
      AUTO_SCORING: "Chấm điểm tự động",
      SKILL_EVALUATION: "Đánh giá kỹ năng",
      TOPIC_PRACTICE: "Luyện tập chủ đề",
      EXPERT_QA: "Hỏi đáp chuyên gia",
    }
    return labelMap[code] || code
  }

  const columns: ColumnsType<FeatureResponse> = [
    {
      title: "Tên tính năng",
      dataIndex: "name",
      width: "25%",
      align: "center",
      sorter: true,
      sortOrder: getColumnSorter("name"),
    },
    {
      title: "Mô tả",
      dataIndex: "description",
      width: "30%",
      align: "left",
      render: (text) => (
        <div className="max-w-xs truncate" title={text}>
          {text || "-"}
        </div>
      ),
    },
    {
      title: "URL",
      dataIndex: "url",
      width: "20%",
      align: "left",
      render: (text) => (
        <div className="max-w-xs truncate" title={text}>
          {text || "-"}
        </div>
      ),
    },
    {
      title: "Mã tính năng",
      dataIndex: "featureCode",
      align: "center",
      render: (code: FeatureCode) => <Tag color={getFeatureCodeColor(code)}>{getFeatureCodeLabel(code)}</Tag>,
      sorter: true,
      sortOrder: getColumnSorter("featureCode"),
    },
    {
      title: "Tham số",
      dataIndex: "params",
      align: "center",
      sorter: true,
      sortOrder: getColumnSorter("params"),
    },
    {
      title: "Hành động",
      dataIndex: "action",
      align: "center",
      render: (_, record) => (
        <div className="flex item-center w-full justify-center gap-2">
          <button className="p-2 rounded bg-[#cdf1ff]" onClick={(e) => moveEditView(e, record)}>
            <EditOutlined className="" />
          </button>
        </div>
      ),
    },
  ]

  const moveEditView = async (e: any, record: FeatureResponse) => {
    setMode(ModePage.EDIT)
    setOpenModal(true)
    setFeatureId(record.id)
  }

  const loadListFeatures = async () => {
    setLoading(true)
    try {
      const res = await axiosInstance({
        url: "features/page",
        params: {
          keyword,
          size: pageSize,
          page: currentPage,
          sortBy,
          direction,
          fromCMS: true,
        },
      })

      if (res.status === 200) {
        setData(res.data.content)
        setTotalElements(res.data.totalElements)
      }
    } catch (err) {
      processError(err)
    }
    setLoading(false)
  }

  const onSearch = (e: any) => {
    setKeyword(e.target.value)
  }

  const addNewFeature = async () => {
    setMode(ModePage.ADD)
    setOpenModal(true)
  }

  const handleCloseModal = () => {
    setOpenModal(false)
  }

  const handleBeforeSuccess = () => {
    setOpenModal(false)
    loadListFeatures().then()
  }

  useEffect(() => {
    loadListFeatures().then()
  }, [currentPage, pageSize, sortBy, direction, keyword])

  return (
    <div className="w-full h-[calc(100vh-112px)] overflow-y-auto p-4">
      <div className="p-2 flex items-center justify-between bg-white rounded">
        <div className="flex gap-4">
          <button
            className="py-1 bg-[#cdf1ff] hover:bg-[#bbe7fa] flex items-center rounded px-2"
            onClick={() => addNewFeature()}
          >
            <PlusOutlined className="text-[15px]" />
            <span className="pl-2">Thêm mới</span>
          </button>
        </div>
        <Input placeholder="Nhập từ khóa tìm kiếm" name="keyword" className="w-fit" onPressEnter={onSearch} />
      </div>
      <div className="w-full bg-white mt-4 rounded p-2 overflow-y-auto h-[calc(100%-60px)]">
        <Table
          columns={columns}
          rowKey={(record) => record.id}
          dataSource={data}
          pagination={pagination}
          loading={loading}
          onChange={handleTableChange}
        />
      </div>
      <FeatureFormModal
        isOpen={isOpen}
        onCloseModal={handleCloseModal}
        mode={mode}
        onBeforeSuccess={handleBeforeSuccess}
        featureId={featureId}
      />
    </div>
  )
}
